import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './App.css';
import { getText } from './i18n';
import DailyFlagGame from './components/DailyFlagGame';
import DailyLanding from './components/DailyLanding';

function App() {
  const [currentFlag, setCurrentFlag] = useState(null);
  const [userGuess, setUserGuess] = useState('');
  const [loading, setLoading] = useState(true);
  const [allCountries, setAllCountries] = useState([]);
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [language, setLanguage] = useState('en');
  const [recentFlags, setRecentFlags] = useState(() => {
    if (typeof window === 'undefined') {
      return [];
    }
    try {
      return JSON.parse(window.localStorage.getItem('classicRecentFlags')) || [];
    } catch {
      return [];
    }
  });
  const [classicStats, setClassicStats] = useState(() => {
    if (typeof window === 'undefined') {
      return { played: 0, won: 0, streak: 0 };
    }
    try {
      return JSON.parse(window.localStorage.getItem('classicStats')) || { played: 0, won: 0, streak: 0 };
    } catch {
      return { played: 0, won: 0, streak: 0 };
    }
  });
  const classicDropdownRef = useRef(null);
  const langDropdownRef = useRef(null);
  const classicInputRef = useRef(null);
  const [inputAnimation, setInputAnimation] = useState(null); // 'correct', 'incorrect', or null
  const [activePage, setActivePage] = useState(() => {
    if (typeof window === 'undefined') return 'daily';
    const hash = window.location.hash.replace('#', '');
    return ['daily', 'classic'].includes(hash) ? hash : 'daily';
  });

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'es', label: 'Español' }
  ];

  const regionLabels = {
    Europe: { en: 'Europe', fr: 'Europe', es: 'Europa' },
    Asia: { en: 'Asia', fr: 'Asie', es: 'Asia' },
    Africa: { en: 'Africa', fr: 'Afrique', es: 'África' },
    'North America': { en: 'North America', fr: 'Amérique du Nord', es: 'América del Norte' },
    'South America': { en: 'South America', fr: 'Amérique du Sud', es: 'América del Sur' },
    Oceania: { en: 'Oceania', fr: 'Océanie', es: 'Oceanía' }
  };

  const getRegionLabel = (region) => {
    if (region === 'all') {
      return getText('allFlags', language);
    }

    return regionLabels[region]?.[language] || region;
  };

  const changeRoute = (route) => {
    if (typeof window !== 'undefined') {
      window.location.hash = route;
    }
    setActivePage(route);
  };

  useEffect(() => {
    const onHashChange = () => {
      if (typeof window !== 'undefined') {
        const hash = window.location.hash.replace('#', '');
        setActivePage(['daily', 'classic'].includes(hash) ? hash : 'home');
      }
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    const pageTitle = activePage === 'daily'
      ? `${getText('dailyFlag', language)} | FastFlagFast`
      : activePage === 'classic'
        ? `${getText('classicMode', language)} | FastFlagFast`
        : `FastFlagFast`;

    document.title = pageTitle;

    const description = activePage === 'daily'
      ? getText('dailyLandingSubtitle', language)
      : getText('dailyClassicDescription', language);

    const updateMeta = (attrName, value, isProperty = false) => {
      const selector = isProperty ? `meta[property="${attrName}"]` : `meta[name="${attrName}"]`;
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        if (isProperty) {
          element.setAttribute('property', attrName);
        } else {
          element.setAttribute('name', attrName);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('content', value);
    };

    updateMeta('description', description);
    updateMeta('og:title', pageTitle, true);
    updateMeta('og:description', description, true);
  }, [activePage, language]);

  const normalizeAnswer = (answer) => {
    if (typeof answer !== 'string') {
      return '';
    }

    return answer
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  };

  const getLocalizedCountryNames = (flag) => {
    if (!flag) {
      return [];
    }

    const names = [flag.name];
    if (flag.names) {
      names.push(...Object.values(flag.names));
    }

    return Array.from(new Set(names.filter(Boolean)));
  };

  const getLocalizedCountryName = (flag) => {
    if (!flag) {
      return '';
    }

    return (flag.names && (flag.names[language] || flag.names.en)) || flag.name || '';
  };

  const getFlagDisplayName = (flag) => {
    if (!flag) {
      return '';
    }

    return getLocalizedCountryName(flag) || flag.name || '';
  };

  const getLocalizedCountryList = () => allCountries.map((country) => {
    if (typeof country === 'string') {
      return {
        name: country,
        flag_code: null,
        displayName: country,
        names: {}
      };
    }

    return {
      ...country,
      displayName: (country.names && (country.names[language] || country.names.en)) || country.name
    };
  });

  const findCountryByFlagPath = useCallback((flagPath) => allCountries.find((country) => country.flag_path === flagPath), [allCountries]);

  const resolveFlag = useCallback((flag) => {
    if (!flag || !flag.flag_path) {
      return null;
    }

    const flagCountry = findCountryByFlagPath(flag.flag_path);
    return flagCountry ? { ...flagCountry, flag_url: flag.flag_url } : flag;
  }, [findCountryByFlagPath]);

  const isCorrectGuess = (guess) => {
    if (!currentFlag || !allCountries.length) {
      return false;
    }

    const actualFlag = findCountryByFlagPath(currentFlag.flag_path);
    if (!actualFlag) {
      return false;
    }

    const answer = normalizeAnswer(guess);
    const acceptedNames = [
      actualFlag.name,
      ...getLocalizedCountryNames(actualFlag),
    ]
      .filter(Boolean)
      .map(normalizeAnswer);

    // Accepted names include the raw metadata name and all localized names.
    return acceptedNames.includes(answer);
  };

  // URL de l'API - détecte automatiquement le domaine courant
  const API_BASE_URL = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}` 
    : 'http://localhost:3000';

  const fetchAllCountries = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/countries`);
      setAllCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const CLASSIC_STATS_KEY = 'classicStats';

  const updateClassicStats = (won) => {
    setClassicStats((prev) => {
      const next = {
        played: prev.played + 1,
        won: prev.won + (won ? 1 : 0),
        streak: won ? prev.streak + 1 : 0,
      };
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(CLASSIC_STATS_KEY, JSON.stringify(next));
      }
      return next;
    });
  };

  const fetchNewFlag = async () => {
    try {
      setLoading(true);
      const endpoint = selectedRegion === 'all' ? '/flag' : `/flag/${selectedRegion}`;
      const response = await axios.get(`${API_BASE_URL}${endpoint}`);
      const flagData = {
        ...response.data,
        flag_url: `${API_BASE_URL}/${response.data.flag_path}`
      };
      setCurrentFlag(flagData);
    } catch (error) {
      console.error('Error fetching flag:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentFlag || !allCountries.length) {
      return;
    }

    const resolvedFlag = resolveFlag(currentFlag);
    if (resolvedFlag && (!currentFlag.name || !currentFlag.names)) {
      setCurrentFlag(resolvedFlag);
    }
  }, [currentFlag, allCountries, resolveFlag]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const isCorrect = isCorrectGuess(userGuess);

    // Déclencher l'animation
    setInputAnimation(isCorrect ? 'correct' : 'incorrect');

    // Réinitialiser l'animation après le temps de la transition
    setTimeout(() => {
      setInputAnimation(null);
    }, 1100);

    if (isCorrect) {
      addRecentFlag(currentFlag, 'won');
      updateClassicStats(true);
      setUserGuess('');
      setCurrentSuggestions([]);
      setSuggestionIndex(0);
      fetchNewFlag();
      setTimeout(() => classicInputRef.current?.focus(), 20);
    } else {
      updateClassicStats(false);
      setUserGuess('');
      setCurrentSuggestions([]);
      setSuggestionIndex(0);
      setTimeout(() => classicInputRef.current?.focus(), 20);
    }
  };

  const handleSkip = () => {
    addRecentFlag(currentFlag, 'skipped');
    updateClassicStats(false);
    setUserGuess('');
    setCurrentSuggestions([]);
    setSuggestionIndex(0);
    fetchNewFlag();
    setTimeout(() => classicInputRef.current?.focus(), 20);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      if (userGuess) {
        const matches = getLocalizedCountryList()
          .filter((country) => {
            const normalizedGuess = userGuess.toLowerCase();
            return country.displayName.toLowerCase().startsWith(normalizedGuess);
          })
          .sort((a, b) => a.displayName.localeCompare(b.displayName, language));

        if (matches.length === 0) {
          return;
        }

        if (currentSuggestions.length === 0) {
          setCurrentSuggestions(matches.map((country) => country.displayName));
          setSuggestionIndex(0);
          setUserGuess(matches[0].displayName);
        } else {
          const nextIndex = (suggestionIndex + 1) % currentSuggestions.length;
          setSuggestionIndex(nextIndex);
          setUserGuess(currentSuggestions[nextIndex]);
        }
      }
    } else {
      setCurrentSuggestions([]);
      setSuggestionIndex(0);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const regionsResponse = await axios.get(`${API_BASE_URL}/regions`);
        setRegions(regionsResponse.data);
      } catch (error) {
        console.error('Error fetching regions:', error);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchNewFlag();
    fetchAllCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion]);

  useEffect(() => {
    setCurrentSuggestions([]);
    setSuggestionIndex(0);
  }, [language]);

  useEffect(() => {
    if (!showDropdown && !showLangDropdown) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (classicDropdownRef.current && !classicDropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setShowLangDropdown(false);
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown, showLangDropdown]);

  const addRecentFlag = (flag, status) => {
    if (!flag) {
      return;
    }

    const resolvedFlag = resolveFlag(flag) || flag;
    const entry = {
      id: `${resolvedFlag.flag_code || resolvedFlag.name || resolvedFlag.flag_path}-${Date.now()}`,
      name: getFlagDisplayName(resolvedFlag),
      flag_url: resolvedFlag.flag_url,
      status,
      showAnswer: status !== 'lost',
      playedAt: new Date().toISOString(),
    };

    setRecentFlags((previous) => {
      const next = [entry, ...previous].slice(0, 5);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('classicRecentFlags', JSON.stringify(next));
      }
      return next;
    });
  };

  return (
    <div className="app-container">
      <nav className="navbar">
        <div className="nav-left">
          <button
            type="button"
            className="nav-brand"
            onClick={() => changeRoute('daily')}
          >
            &#127758; {getText('navBrand', language)}
          </button>
          <div className="mode-tabs">
            <button
              type="button"
              className={`mode-button ${activePage === 'daily' ? 'active' : ''}`}
              onClick={() => {
                setShowDropdown(false);
                changeRoute('daily');
              }}
            >
              {getText('dailyFlag', language)}
            </button>
            <div className="dropdown classic-dropdown" ref={classicDropdownRef}>
              <button
                type="button"
                className={`mode-button ${activePage === 'classic' ? 'active' : ''}`}
                onClick={() => {
                  setShowDropdown((prev) => !prev);
                  changeRoute('classic');
                }}
              >
                {getText('classicMode', language)}
                <span className="dropdown-arrow">▼</span>
              </button>
              {showDropdown && (
                <div className="dropdown-menu">
                  <div
                    className={`dropdown-item ${selectedRegion === 'all' ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedRegion('all');
                      setShowDropdown(false);
                    }}
                  >
                    {getText('allFlags', language)}
                  </div>
                  {regions.map(region => (
                    <div
                      key={region}
                      className={`dropdown-item ${selectedRegion === region ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedRegion(region);
                        setShowDropdown(false);
                      }}
                    >
                      {getRegionLabel(region)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="nav-right">
          <div className="dropdown lang-dropdown" ref={langDropdownRef}>
            <button
              className="mode-button"
              onClick={() => setShowLangDropdown(!showLangDropdown)}
            >
              {languages.find((lang) => lang.code === language)?.label}
              <span className="dropdown-arrow">▼</span>
            </button>
            {showLangDropdown && (
              <div className="dropdown-menu">
                {languages.map((lang) => (
                  <div
                    key={lang.code}
                    className={`dropdown-item ${language === lang.code ? 'selected' : ''}`}
                    onClick={() => {
                      setLanguage(lang.code);
                      setShowLangDropdown(false);
                    }}
                  >
                    {lang.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className="main-content">
        {activePage === 'home' && (
          <DailyLanding
            language={language}
            onPlayDaily={() => changeRoute('daily')}
            onPlayClassic={() => changeRoute('classic')}
          />
        )}

        {activePage === 'daily' && (
          <DailyFlagGame
            allCountries={allCountries}
            language={language}
            getLocalizedCountryNames={getLocalizedCountryNames}
            getLocalizedCountryName={getLocalizedCountryName}
            onReturnHome={() => changeRoute('daily')}
          />
        )}

        {activePage === 'classic' && (
          <div className="flag-quiz">
            {classicStats.played > 0 && (
              <div className="classic-stats-panel">
                <div className="classic-stat-card">
                  <span className="classic-stat-icon">🔥</span>
                  <div className="classic-stat-copy">
                    <span className="classic-stat-value">{classicStats.streak}</span>
                    <span className="classic-stat-label">{getText('classicStreakLabel', language)}</span>
                  </div>
                </div>
                <div className="classic-stat-card">
                  <span className="classic-stat-icon">🎯</span>
                  <div className="classic-stat-copy">
                    <span className="classic-stat-value">{`${Math.round((classicStats.won / classicStats.played) * 100)}%`}</span>
                    <span className="classic-stat-label">{getText('classicSuccessLabel', language)}</span>
                  </div>
                </div>
              </div>
            )}
            <div className={`flag-container${loading ? ' loading' : ''}`}>
              {currentFlag && (
                <img
                  src={currentFlag.flag_url}
                  alt={getText('placeholder', language)}
                />
              )}
              {loading && (
                <div className="flag-loading-state">
                  <span className="flag-loading-text">{getText('loading', language)}</span>
                </div>
              )}
              {!loading && !currentFlag && (
                <div className="flag-error-state">{getText('errorLoading', language)}</div>
              )}
            </div>
            <form onSubmit={handleSubmit} className="form-wrapper">
              <div className="form-input-group">
                <input
                  ref={classicInputRef}
                  type="text"
                  value={userGuess}
                  onChange={(e) => setUserGuess(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={getText('placeholder', language)}
                  autoFocus
                  className={inputAnimation || ''}
                  disabled={!currentFlag}
                />
                <button
                  type="submit"
                  className="guess-button"
                  disabled={!currentFlag}
                >
                  {getText('dailyGuessButton', language)}
                </button>
                <button
                  type="button"
                  className="skip-button secondary"
                  onClick={handleSkip}
                  disabled={loading || !currentFlag}
                >
                  {getText('skip', language)}
                </button>
              </div>
            </form>
          </div>
        )}

        {activePage === 'classic' && recentFlags.length > 0 && (
          <div className="recent-flags">
            <div className="recent-flags-header">
              {getText('recentFlagsTitle', language)}
            </div>
            <div className="recent-flags-grid">
              {recentFlags.map((entry) => (
                <div key={entry.id} className="recent-flag-entry">
                  <div className="recent-flag-swatch">
                    {entry.showAnswer ? (
                      <img src={entry.flag_url} alt={entry.name} />
                    ) : (
                      <div className="recent-flag-swatch-placeholder">?</div>
                    )}
                  </div>
                  <div className="recent-flag-details">
                    {entry.showAnswer && <span className="recent-flag-name">{entry.name}</span>}
                    <span className={`recent-flag-status ${entry.status}`}>
                      {entry.status === 'won'
                        ? getText('won', language)
                        : getText('lost', language)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;